import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import styled from 'styled-components'

import Checkbox from '@mui/material/Checkbox'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import {
  useCheckEmailCert,
  useCheckEmailKeyCertCode,
  useNicknameDuplicateCheck,
  useSignUp,
} from '~/features/auth/queries/useAuth'
import Close from '~/shared/assets/icons/Close'
import Eye from '~/shared/assets/icons/Eye'
import Naver from '~/shared/assets/logos/naver.png'
import Button from '~/shared/components/buttons/Button'
import Dropdown from '~/shared/components/dropdown/Dropdown'
import { checkEmailValidation } from '~/shared/components/modals/auth/checkEmailValidation'
import PasswordValidationCheck from '~/shared/components/modals/auth/PasswordValidationCheck'
import { isModalOpenState, keyCodeState } from '~/shared/states'
import theme from '~/shared/styles/theme'

type Inputs = {
  nickname: string
  name: string
  gender: string
  birthYear: number
  email: string
  password: string
  passwordCheck: string
}

function SignUpModal() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const setIsModalOpen = useSetRecoilState(isModalOpenState)
  const [passwordChangeKeyCode, setPasswordChangeKeyCode] =
    useRecoilState(keyCodeState)

  const { mutate: checkEmailCert, isPending } = useCheckEmailCert()
  const { mutate: nicknameDuplicateCheck } = useNicknameDuplicateCheck()
  const { mutate: checkEmailKeyCertCode } = useCheckEmailKeyCertCode()

  const { mutate: signUpMutate } = useSignUp()

  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState<boolean>(false)
  const [isPasswordValidated, setIsPasswordValidated] = useState<boolean>(false)
  const [isPasswordMatched, setIsPasswordMatched] = useState<boolean>(false)
  const [isEmailCertificated, setIsEmailCertificated] = useState<boolean>(false)
  const [emailValidatedMsg, setEmailValidatedMsg] = useState<string>('')
  const [nicknameValidatedMsg, setNicknameValidatedMsg] = useState<string>('')

  const { register, handleSubmit, watch, setValue } = useForm<Inputs>()

  const [nickname, name, gender, birthYear, email, password, passwordCheck] =
    watch([
      'nickname',
      'name',
      'gender',
      'birthYear',
      'email',
      'password',
      'passwordCheck',
    ])

  const checkEmailValidated = (emailInput: string) => {
    if (emailInput) {
      enqueueSnackbar('인증 메일 전송을 요청합니다.', {
        variant: 'success',
      })

      checkEmailCert(emailInput, {
        onSuccess: (response) => {
          if (response.errorCode < 0) {
            setEmailValidatedMsg(response.errorMsg)
          } else if (response.errorCode === 0) {
            if (!checkEmailValidation(emailInput)) {
              setEmailValidatedMsg('이메일 형식이 올바르지 않습니다.')
            } else {
              enqueueSnackbar('인증 메일 전송이 완료되었습니다.', {
                variant: 'success',
              })
              setPasswordChangeKeyCode({
                ...passwordChangeKeyCode,
                key: response.data.key,
              })
              setEmailValidatedMsg('')
            }
          }
        },
        onError: () => {
          enqueueSnackbar('에러가 발생했습니다. 다시 시도해주세요.', {
            variant: 'error',
          })
        },
      })
    }
  }

  const checkCertCode = () => {
    checkEmailKeyCertCode(
      { key: passwordChangeKeyCode.key, code: passwordChangeKeyCode.code },
      {
        onSuccess: (response) => {
          if (response.errorCode < 0) {
            enqueueSnackbar(response.errorMsg, {
              variant: 'error',
            })
          } else if (response.errorCode === 0) {
            enqueueSnackbar('이메일 인증에 성공했습니다.', {
              variant: 'success',
            })
            setIsEmailCertificated(true)
          }
        },
        onError: () => {
          enqueueSnackbar('에러가 발생했습니다. 다시 시도해주세요.', {
            variant: 'error',
          })
          setIsEmailCertificated(false)
        },
      },
    )
  }

  const checkNicknameValidated = (nicknameInput: string) => {
    if (nicknameInput.length > 0) {
      nicknameDuplicateCheck(nicknameInput, {
        onSuccess: (response) => {
          if (response.errorCode === -2003) {
            setNicknameValidatedMsg(response.errorMsg)
          } else {
            setNicknameValidatedMsg('')
          }
        },
        onError: () => {
          enqueueSnackbar('에러가 발생했습니다. 다시 시도해주세요.', {
            variant: 'error',
          })
        },
      })
    }
  }

  const changeNicknameInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setNicknameValidatedMsg('닉네임 중복 체크가 필요합니다.')
      setValue('nickname', e.target.value)
    }
  }

  const changeEmailInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setEmailValidatedMsg('')
      setIsEmailCertificated(false)
      setValue('email', e.target.value)
    }
  }

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    if (isTermsAgreed === false) {
      enqueueSnackbar('약관에 동의해 주세요.', {
        variant: 'error',
      })
    } else if (isPasswordValidated === false) {
      enqueueSnackbar('비밀번호가 조건에 맞지 않습니다.', {
        variant: 'error',
      })
    } else if (
      !(
        nickname &&
        name &&
        gender &&
        birthYear &&
        email &&
        password &&
        passwordCheck &&
        isPasswordMatched &&
        isEmailCertificated
      )
    ) {
      enqueueSnackbar('모든 항목을 입력해주세요.', {
        variant: 'error',
      })
    } else {
      signUpMutate(
        {
          userInfo: {
            name: data.name,
            nickname: data.nickname,
            email: data.email,
            password: data.password,
            gender: data.gender || 'M',
            year_of_birth: data.birthYear.toString(),
          },
          key: passwordChangeKeyCode.key,
          certCode: passwordChangeKeyCode.code,
        },
        {
          onSuccess: (response) => {
            if (response.errorCode < 0) {
              enqueueSnackbar(response.errorMsg, {
                variant: 'error',
              })
            } else if (response.errorCode === 0) {
              enqueueSnackbar('회원가입이 완료되었습니다.', {
                variant: 'success',
              })
              setIsModalOpen({ contents: '', isOpen: false })
              navigate('/auth/sign-up-completed')
            }
          },
        },
      )
    }
  }

  const signInNaver = async () => {
    window.location.href = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${import.meta.env.VITE_NAVER_CLIENT_ID}&state=starlike&redirect_uri=${import.meta.env.VITE_REDIRECT_URI}`
  }

  useEffect(() => {
    if (password === passwordCheck) {
      setIsPasswordMatched(true)
    } else {
      setIsPasswordMatched(false)
    }
  }, [password, passwordCheck])

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <CloseIcon
        onClick={() => setIsModalOpen({ contents: '', isOpen: false })}
      />
      <Container>
        <NaverSignInButtonBox>
          <img src={Naver} alt="naver" width={42} height={42} />
          <NaverSignInButton onClick={signInNaver}>
            네이버로 바로 회원가입
          </NaverSignInButton>
        </NaverSignInButtonBox>

        <Title>회원가입</Title>
        <InputBox onSubmit={handleSubmit(onSubmit)}>
          <div>
            <InputTitle>닉네임</InputTitle>
            <Input
              placeholder="닉네임"
              className={`${nicknameValidatedMsg && 'warning'}`}
              type="text"
              onChange={changeNicknameInput}
            />
            {nicknameValidatedMsg && (
              <WarningText>*{nicknameValidatedMsg}</WarningText>
            )}
          </div>

          <Button
            $width={141}
            $height={58}
            $fontSize={18}
            $variant="white"
            type="button"
            onClick={() => checkNicknameValidated(nickname)}
          >
            {nicknameValidatedMsg ? '중복 확인' : '확인됨'}
          </Button>

          <div>
            <InputTitle>이름</InputTitle>
            <Input type="text" placeholder="이름" {...register('name')} />
          </div>
          <div>
            <InputTitle>성별</InputTitle>
            <GenderDropdown
              options={[
                { value: 'F', label: '여성' },
                { value: 'M', label: '남성' },
              ]}
              {...register('gender')}
            />
          </div>
          <div>
            <InputTitle>출생년도</InputTitle>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledDatePicker
                disableFuture
                views={['year']}
                onChange={(e) => {
                  if (e) {
                    setValue('birthYear', e.year())
                  }
                }}
              />
            </LocalizationProvider>
          </div>
          <div>
            <InputTitle>이메일</InputTitle>
            <Input
              className={`${emailValidatedMsg && 'warning'}`}
              type="email"
              placeholder="이메일"
              onChange={changeEmailInput}
            />
            {emailValidatedMsg && (
              <WarningText>*{emailValidatedMsg}</WarningText>
            )}
          </div>
          {!isPending && (
            <CertButton
              $width={141}
              $height={58}
              $fontSize={18}
              $variant="white"
              $disabled={isEmailCertificated}
              type="button"
              onClick={() => checkEmailValidated(email)}
              disabled={isEmailCertificated ? true : false}
            >
              {isEmailCertificated ? '인증 완료' : '인증 메일 보내기'}
            </CertButton>
          )}

          {!isEmailCertificated && (
            <>
              <div>
                <InputTitle>인증번호</InputTitle>
                <Input
                  type="text"
                  placeholder="000000"
                  onChange={(e) => {
                    setPasswordChangeKeyCode({
                      ...passwordChangeKeyCode,
                      code: e.target.value,
                    })
                  }}
                />
              </div>
              <Button
                $width={141}
                $height={58}
                $fontSize={18}
                $variant="white"
                type="button"
                onClick={() => checkCertCode()}
              >
                인증 하기
              </Button>
            </>
          )}

          <div>
            <PasswordLabel>
              <InputTitle>비밀번호</InputTitle>
              <Input
                type={isPasswordVisible ? 'text' : 'password'}
                placeholder="비밀번호"
                {...register('password')}
              />
              <EyeIcon
                color={
                  isPasswordVisible ? theme.colors.grey_5 : theme.colors.grey_4
                }
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              />
            </PasswordLabel>
            <PasswordValidationCheck
              passwordInput={password || ''}
              setIsPasswordValidated={setIsPasswordValidated}
            />
          </div>
          <div>
            <PasswordLabel>
              <InputTitle>비밀번호 확인</InputTitle>
              <Input
                type={isPasswordVisible ? 'text' : 'password'}
                className={isPasswordMatched ? '' : 'warning'}
                placeholder="비밀번호 확인"
                {...register('passwordCheck')}
              />
              <EyeIcon
                color={
                  isPasswordVisible ? theme.colors.grey_5 : theme.colors.grey_4
                }
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
              />
            </PasswordLabel>
          </div>

          <InputDesc className={`${!isPasswordMatched && 'warning'}`}>
            {!isPasswordMatched && '*패스워드가 일치하지 않습니다.'}
          </InputDesc>
          <TermsAgreementBox>
            <a
              target="_blank"
              href={`${import.meta.env.VITE_REDIRECT_URI}terms`}
            >
              일러스타 페스 안내 및 이용약관 동의
            </a>
            <StyledCheckbox
              onChange={(e) => setIsTermsAgreed(e.target.checked)}
            />
          </TermsAgreementBox>
          <SignUpButton
            type="submit"
            value="회원가입"
            $isactived={
              nickname &&
              name &&
              gender &&
              birthYear &&
              email &&
              password &&
              passwordCheck &&
              isPasswordMatched
                ? true
                : false
            }
          />
        </InputBox>
      </Container>
    </Wrapper>
  )
}

export default SignUpModal

const Wrapper = styled.section`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  box-sizing: border-box;
  width: 500px;
  height: auto;
  max-height: 80vh;
  margin: 0 auto;
  padding: 80px 77px 60px;

  background-color: ${theme.colors.white};
  border-radius: 12px;

  @media ${theme.device.medium} {
    top: 50%;
    width: 90%;
    height: auto;
    padding: 56px 20px 30px;
  }
`

const Container = styled.div`
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  width: 100%;
`

const CloseIcon = styled(Close)`
  cursor: pointer;
  position: absolute;
  top: 36px;
  right: 36px;

  @media ${theme.device.medium} {
    top: 20px;
    right: 20px;
    width: 24px;
    height: 24px;
  }
`

const CertButton = styled(Button)<{ $disabled: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};

  width: 100%;
  height: 46px;

  font-size: 18px;
  color: ${theme.colors.black};

  background-color: ${theme.colors.white};
  border: ${({ $disabled }) =>
    $disabled ? `1px solid ${theme.colors.grey_3}` : '1px solid black'};
`

const Title = styled.h1`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 500;

  @media ${theme.device.medium} {
    font-size: 36px;
  }
`

const InputBox = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const InputTitle = styled.div`
  bottom: 12px;

  margin: 12px 0;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  color: ${theme.colors.grey_4};
`

const GenderDropdown = styled(Dropdown)`
  width: 100%;
  height: 49px;
  color: ${theme.colors.grey_5};
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  background-color: ${theme.colors.grey_1};
  border-radius: 12px;

  /* stylelint-disable-next-line selector-class-pattern */
  & .MuiOutlinedInput-root {
    border: none;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`

const PasswordLabel = styled.label`
  position: relative;
`

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 49px;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;

  background-color: ${theme.colors.grey_1};
  border: none;
  border-radius: 12px;

  &.warning {
    border: 1px solid ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const InputDesc = styled.div`
  display: flex;
  align-items: center;
  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;

  &.warning {
    color: ${theme.colors.warning};
  }
`

const WarningText = styled.div`
  margin: 10px 5px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.warning};
`

const EyeIcon = styled(Eye)`
  position: absolute;
  top: 85%;
  right: 20px;
  transform: translateY(-50%);
`

const TermsAgreementBox = styled.div`
  cursor: pointer;

  display: flex;
  align-items: center;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;

  a {
    color: ${theme.colors.grey_4};
    text-decoration: underline;
  }
`

const StyledCheckbox = styled(Checkbox)`
  &.css-12wnr2w-MuiButtonBase-root-MuiCheckbox-root.Mui-checked,
  .css-12wnr2w-MuiButtonBase-root-MuiCheckbox-root.MuiCheckbox-indeterminate {
    color: ${theme.colors.mint_3};
  }
`

const SignUpButton = styled.input<{ $isactived: boolean }>`
  cursor: pointer;

  gap: 4px;

  width: 100%;
  height: 48px;
  margin-top: 12px;
  padding: 8px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: ${theme.colors.white};

  background-color: ${({ $isactived }) =>
    $isactived ? theme.colors.grey_5 : theme.colors.grey_1};
  border: none;
  border-radius: 50px;

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const NaverSignInButtonBox = styled.div`
  cursor: pointer;

  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 100%;
  height: 82px;
  padding: 20px;

  border-bottom: 1px solid ${theme.colors.grey_3};
`

const NaverSignInButton = styled.button`
  cursor: pointer;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  color: ${theme.colors.grey_5};

  background-color: transparent;
  border: none;

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`
